import { API } from '@/services';
import { UPDATE_ENTITY } from '@/services/account/mutations/account/index.js';
import { UPDATE_NOTIFICATION_USERS_MUTATION } from '@/services/project/mutations/notification/index.js';
import { REMOVE_PROJECT_PRODUCT_MUTATION } from '@/services/project/mutations/product/index.js';
import {
  ATTACH_PROJECT_ENTITY_MAPPING,
  COPY_PROJECT,
  CREATE_PROJECT_VIEWS_MUTATION,
  DELETE_PROJECT_MUTATION,
  DELETE_PROJECT_VIEW_MUTATION,
  DETACH_PROJECT_ENTITY_MAPPING,
  UPDATE_PROJECT_MUTATION,
  UPDATE_PROJECT_VIEWS_MUTATION,
} from '@/services/project/mutations/project/index.js';
import { GET_PROJECT_CREATOR_INVITES_QUERY } from '@/services/project/queries/creator/index.js';
import {
  GET_PROJECT_BRIEF_FILES_QUERY,
  GET_PROJECT_BRIEF_LINK_QUERY,
  GET_PROJECT_NOTIFICATION_USERS,
  GET_PROJECT_PRODUCTS_QUERY,
  GET_PROJECT_QUERY,
  GET_PROJECT_VIEWS_QUERY,
} from '@/services/project/queries/index.js';
import { GET_ACCOUNT_TEAM_MEMBERS_NO_PROJECT_QUERY, GET_WORKSPACE_TEAM_MEMBERS_NO_PROJECT_QUERY } from '@/services/workspace/queries/workspace/index.js';
import { useAccountStore } from '@/stores/account.store.js';
import { useProjectDeliverablesStore } from '@/stores/project/deliverables.store.js';
import { useProjectDetailsStore } from '@/stores/project/details.store.js';
import { useProjectDocumentsStore } from '@/stores/project/documents.store.js';
import { useUserStore } from '@/stores/user.store.js';
import { useWorkspaceStore } from '@/stores/workspace.store.js';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

interface ProjectTheme {
  color?: string;
  [key: string]: any;
}

interface Project {
  id: number;
  name: string;
  theme: ProjectTheme;
  workspace?: {
    id: number;
    name: string;
    image?: string;
    archived?: boolean;
  };
  account?: {
    id: number;
    name: string;
    image?: string;
    type?: string;
  };
  editMenu?: boolean;
  isRemoving?: boolean;
  isLoading?: boolean;
}

interface Product {
  id: number;
  name: string;
  image_url: string;
  isRemoved: boolean;
  isLoading: boolean;
}

interface BriefFile {
  id: number;
  filename: string;
  thumbnail?: string;
  tag?: string;
  type?: string;
}

interface ProjectView {
  id: number;
  name: string;
  project_id: number;
  grouping: any;
  hidden_fields: any;
  column_state: any;
  filter: any;
  sort: any;
  shared: boolean;
  locked: boolean;
  blocked: any[];
  isEditingName: boolean;
  newName: string;
  isLoading: boolean;
  isRemoved: boolean;
  isDuplicating: boolean;
  editMenu: boolean;
}

interface TempUploadedData {
  creators: any[];
  products: any[];
  entity_mapping: any[];
  lockedCustomColumns: any[];
  originalCreators: any[];
  originalProducts: any[];
}

export const useProjectStore = defineStore('projects', () => {
  const currentProjectID = ref<number | null>(null);
  const isProjectLoading = ref(false);
  const isFetchingProjects = ref(false);
  const isCreatorDetailsViewActive = ref(true);
  const isDocumentsViewActive = ref(true);
  const isDeliverablesViewActive = ref(true);
  const projects = ref<Project[]>([]);
  const activeProject = ref<Project | null>(null);
  const activeWorkspace = ref<any | null>(null);
  const activeAccount = ref<any | null>(null);
  const activeTab = ref<string | null>(null);
  const activeTeamMembers = ref<any[]>([]);
  const projectCreatorInvites = ref<any[]>([]);
  const projectCreatorInviteSent = ref(false);
  const isLoadingCreatorInvites = ref(false);
  const tempEntityMappings = ref<any[]>([]);
  const views = ref<ProjectView[]>([]);
  const briefFiles = ref<BriefFile[]>([]);
  const products = ref<Product[]>([]);
  const isGridUpdating = ref(false);
  const isCreatingProjectView = ref(false);
  const teamMembers = ref([]);
  const workspaceMembers = ref([]);
  const notificationTeamMembers = ref([]);
  const notificationClientUsers = ref([]);
  const isUpdatingProjectNotificationUsers = ref(false);
  const selectedNotificationTeamMembers = ref([]);
  const selectedNotificationClientUsers = ref([]);
  const tempUploadedData = ref<TempUploadedData>({
    creators: [],
    products: [],
    entity_mapping: [],
    lockedCustomColumns: [],
    originalCreators: [],
    originalProducts: [],
  });
  const tempLockedCustomColumns = ref<any[]>([]);
  const showViewsPanel = ref<boolean>(localStorage.getItem('_pup_project_views_hidden') ? JSON.parse(localStorage.getItem('_pup_project_views_hidden')!) : true);

  // Column Edit Menu State and Logic
  const showEditColumnMenu = ref(false);
  const columnMenuAnchor = ref(null); // The element to anchor the menu to
  const columnToEdit = ref(null);

  function openMenu(anchorEl, columnDef) {
    columnMenuAnchor.value = anchorEl;
    columnToEdit.value = { ...columnDef };
    showEditColumnMenu.value = true;
  }

  function closeMenu() {
    showEditColumnMenu.value = false;
    columnMenuAnchor.value = null;
    columnToEdit.value = null;
  }

  const getLocalStorageViewPanel = computed(() => {
    return localStorage.getItem(`_pup_project_${activeProject.value?.id}_views_panel_hidden`)
      ? JSON.parse(localStorage.getItem(`_pup_project_${activeProject.value?.id}_views_panel_hidden`)!)
      : false;
  });

  const hasBrightThemeColor = computed(() => {
    const colors = ['#0FC0DF', '#FDC619', '#5DE1E6'];
    return colors.includes(activeProject.value?.theme?.color);
  });

  const toggleViewsPanel = (bool: boolean) => {
    showViewsPanel.value = bool;
    localStorage.setItem(`_pup_project_${activeProject.value?.id}_views_panel_hidden`, JSON.stringify(bool));
  };

  const setActiveProject = (project: Project | null) => {
    if (project) {
      console.log('setting project', project.id);

      project = {
        ...project,
        id: Number(project.id),
        theme: project?.theme ? JSON.parse(project.theme as string) : {},
        editMenu: false,
        isRemoving: false,
        isLoading: false,
      };

      currentProjectID.value = project.id;
      activeProject.value = project;
      activeWorkspace.value = project?.workspace ?? null;
      activeAccount.value = project?.account ?? null;
    }
  };

  const setActiveTeamMembers = (teamMembers: any[]) => {
    if (teamMembers) {
      activeTeamMembers.value = teamMembers;
    }
  };

  const setProjectCreatorInvites = (creatorInvites: any[]) => {
    if (creatorInvites) {
      projectCreatorInvites.value = creatorInvites;
    }
  };

  const setBriefFiles = (files: BriefFile[]) => {
    if (files) {
      briefFiles.value = files;
    }
  };

  const setProducts = (newProducts: Product[]) => {
    if (newProducts) {
      products.value = newProducts.map((product) => ({
        ...product,
        name: `${product.name?.substring(0, 40)}...`,
        chipName: `${product.name?.substring(0, 10)}...`,
        isRemoved: false,
        isLoading: false,
      }));
    }
  };

  const saveActiveViewInDB = async (newView: ProjectView, oldView: ProjectView | null = null, routeName: string | null = null) => {
    const deliverablesStore = useProjectDeliverablesStore();
    const detailsStore = useProjectDetailsStore();
    const documentsStore = useProjectDocumentsStore();
    const newViewPayload = {
      ...newView,
      custom_properties:
        newView?.custom_properties && newView?.custom_properties.some((p) => p.key === 'Last Used')
          ? newView.custom_properties.map((prop) => {
              if (prop.key === 'Last Used') {
                return { key: 'Last Used', value: ['true'] };
              }
              return prop;
            })
          : [{ key: 'Last Used', value: ['true'] }],
    };
    const oldViewPayload = {
      ...oldView,
      custom_properties:
        oldView?.custom_properties && oldView?.custom_properties.some((p) => p.key === 'Last Used')
          ? oldView?.custom_properties.map((prop) => {
              if (prop.key === 'Last Used') {
                return { key: 'Last Used', value: ['false'] };
              }
              return prop;
            })
          : [{ key: 'Last Used', value: ['false'] }],
    };

    let store;
    if (routeName === 'Project-Creator-Details') {
      store = detailsStore;
    } else if (routeName === 'Project-Deliverables') {
      store = deliverablesStore;
    } else if (routeName === 'Project-Documents') {
      store = documentsStore;
    }

    if (oldView && store.views.some((view) => view.id === oldView.id)) {
      const index = store.views.findIndex((view) => view.id === oldView.id);
      store.views[index] = oldViewPayload;

      updateProjectView({
        id: Number(oldViewPayload.id),
        custom_properties: oldViewPayload.custom_properties,
        isGridLoading: false,
      });
    }

    if (newView && store.views.some((view) => view.id === newView.id)) {
      const index = store.views.findIndex((view) => view.id === newView.id);
      store.views[index] = newViewPayload;

      store.activeView = newViewPayload;

      updateProjectView({
        id: Number(newViewPayload.id),
        custom_properties: newViewPayload.custom_properties,
        isGridLoading: false,
      });
    }
  };

  const formatViews = (views: ProjectView[]): ProjectView[] => {
    let data: ProjectView[] = [];
    if (views.length > 0) {
      data = views.map((view) => ({
        ...view,
        id: Number(view.id),
        project_id: view?.project_id ? Number(view.project_id) : null,
        grouping: JSON.parse(view.grouping as string),
        hidden_fields: JSON.parse(view.hidden_fields as string),
        column_state: view.column_state ? JSON.parse(view.column_state as string) : [],
        filter: JSON.parse(view.filter as string),
        sort: JSON.parse(view.sort as string),
        shared: Boolean(view.shared),
        locked: Boolean(view.locked),
        blocked: view.blocked ? JSON.parse(view.blocked as string) : [],
        isEditingName: false,
        newName: view.name ?? '',
        isLoading: false,
        isRemoved: false,
        isDuplicating: false,
        editMenu: false,
      }));
    }
    return data;
  };

  const getDefaultImportSpreadsheet = computed(() => {
    return 'https://icarus.expotv.com/partnrup/template/project/PUPImportSpreadsheetTemplate.xlsx';
  });

  const savedAccountContext = ref<{ account: any | null; workspace: any | null } | null>(null);

  const syncGlobalAccountContextFromProject = () => {
    if (!activeAccount.value) {
      return;
    }

    const accountStore = useAccountStore();
    const workspaceStore = useWorkspaceStore();
    const projectAccountId = activeAccount.value.id;
    const globalAccountId = accountStore.activeAccount?.id;
    const projectWorkspaceId = activeWorkspace.value?.id ?? null;
    const globalWorkspaceId = workspaceStore.activeWorkspace?.id ?? null;

    if (projectAccountId === globalAccountId && projectWorkspaceId === globalWorkspaceId) {
      return;
    }

    if (!savedAccountContext.value) {
      savedAccountContext.value = {
        account: accountStore.activeAccount,
        workspace: workspaceStore.activeWorkspace,
      };
    }

    accountStore.activeAccount = activeAccount.value;
    workspaceStore.activeWorkspace = activeWorkspace.value ?? null;
  };

  const restoreGlobalAccountContext = () => {
    if (!savedAccountContext.value) {
      return;
    }

    const accountStore = useAccountStore();
    const workspaceStore = useWorkspaceStore();
    accountStore.activeAccount = savedAccountContext.value.account;
    workspaceStore.activeWorkspace = savedAccountContext.value.workspace;
    savedAccountContext.value = null;
  };

  const initProject = async (project_id: number) => {
    try {
      isProjectLoading.value = true;
      await Promise.all([
        getProjectViews(project_id),
        getProject(project_id),
        getProjectProducts(project_id),
        getProjectCreatorInvites(project_id),
        getProjectBriefFiles(project_id),
        getProjectNotificationUsers(project_id),
      ]);
      syncGlobalAccountContextFromProject();
      await getProjectUserPermissions(project_id);
    } catch (error) {
      console.log(error);
      throw error;
    } finally {
      isProjectLoading.value = false;
    }
  };

  const getProjectUserPermissions = async (project_id: number) => {
    try {
      const payload = {
        account_id: Number(activeAccount.value?.id),
        workspace_id: Number(activeWorkspace.value?.id),
        project_id: Number(project_id),
      };
      await useUserStore().getCurrentUserPermissions(payload);
    } catch (error) {
      console.log(error);
    }
  };

  const getProject = async (project_id: number) => {
    try {
      const res = await API.sendQuery('/graphql/project', GET_PROJECT_QUERY, { project_id: project_id });

      if (res.data.errors) {
        console.log('res.data.errors', res.data.errors);
        throw new Error(res.data.errors[0]);
      }

      if (res.data.data.project) {
        setActiveProject(res.data.data.project);
      }

      if (res.data.data.teamMembers) {
        setActiveTeamMembers(res.data.data.teamMembers);
      }
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const getProjectNotificationUsers = async (project_id: number) => {
    try {
      const res = await API.sendQuery('/graphql/project', GET_PROJECT_NOTIFICATION_USERS, { project_id: project_id });
      if (res.data.data.notificationUsers) {
        notificationTeamMembers.value = res.data.data.notificationUsers;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getTeamMembers = async (account_id: number, workspace_id: number) => {
    try {
      const res = await API.sendQuery('/graphql/account', GET_ACCOUNT_TEAM_MEMBERS_NO_PROJECT_QUERY, { account_id: account_id, workspace_id: workspace_id });
      if (res.data.data.teamMembers) {
        teamMembers.value = res.data.data.teamMembers.map((member: any) => ({
          id: member.user.id,
          name: member.user.first_name ? `${member.user.first_name} ${member.user.last_name}` : member.user.username ?? member.user.email,
          avatar: member.user.avatar,
          type_id: member.type_id,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getWorkspaceMembers = async (workspace_id: number, role: string) => {
    try {
      const res = await API.sendQuery('/graphql/workspace', GET_WORKSPACE_TEAM_MEMBERS_NO_PROJECT_QUERY, { workspace_id: workspace_id, role: role });
      if (res.data.data.teamMembers) {
        workspaceMembers.value = res.data.data.teamMembers.map((member: any) => ({
          id: member.user.id,
          name: member.user.first_name ? `${member.user.first_name} ${member.user.last_name}` : member.user.username ?? member.user.email,
          avatar: member.user.avatar,
          type_id: member.type_id,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getProjectProducts = async (project_id: number) => {
    try {
      const res = await API.sendQuery('/graphql/project', GET_PROJECT_PRODUCTS_QUERY, { project_id: project_id });

      if (res.data.data.products) {
        setProducts(res.data.data.products);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getProjectViews = async (project_id: number) => {
    try {
      const res = await API.sendQuery('/graphql/project', GET_PROJECT_VIEWS_QUERY, { project_id: project_id });

      if (res.data.data.views) {
        const creatorDetailViews = res.data.data.views.filter((view: ProjectView) => view.base === 'creator_details');
        const deliverableViews = res.data.data.views.filter((view: ProjectView) => view.base === 'deliverables');
        const documentViews = res.data.data.views.filter((view: ProjectView) => view.base === 'documents');

        useProjectDetailsStore().setViews(formatViews(creatorDetailViews));
        useProjectDeliverablesStore().setViews(formatViews(deliverableViews));
        useProjectDocumentsStore().setViews(formatViews(documentViews));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getProjectBriefFiles = async (project_id: number): Promise => {
    try {
      const res = await API.sendQuery('/graphql/project', GET_PROJECT_BRIEF_FILES_QUERY, { project_id: project_id });
      if (res.data?.data?.briefs) {
        briefFiles.value = res.data.data.briefs.map((file: BriefFile) => ({
          ...file,
          isLoading: false,
        }));
        return briefFiles.value;
      } else {
        console.warn('No uploads found in the API response');
        return [];
      }
    } catch (error) {
      console.error('Error fetching project brief files:', error);
      throw error;
    }
  };

  const downloadProjectBriefFile = async (project_id: number, briefingFileId: number) => {
    try {
      const payload = {
        project_id: Number(project_id),
        upload_id: Number(briefingFileId),
      };
      let result = await API.sendQuery('/graphql/project', GET_PROJECT_BRIEF_LINK_QUERY, payload);
      if (result.errors) {
        throw new Error(result.errors[0]);
      }
      console.log('result', result);
      return result.data.data.downloadBrief;
    } catch (error) {
      console.error('Error fetching project brief file for download:', error);
      throw error;
    }
  };

  const updateProject = async (payload: any) => {
    try {
      const res = await API.sendQuery('/graphql/project', UPDATE_PROJECT_MUTATION, payload);
      if (res.data.data.updateProject) {
        return res.data.data.updateProject;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const copyProject = async (new_project_name: string) => {
    try {
      const payload = {
        project_id: Number(activeProject.value?.id),
        workspace_id: Number(activeProject.value?.workspace?.id),
        name: new_project_name,
      };
      const res = await API.sendQuery('/graphql/project', COPY_PROJECT, payload);
      if (res.data.data.copyProject) {
        return res.data.data.copyProject;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const updateProjectNotificationUsers = async (projectId: number) => {
    try {
      isUpdatingProjectNotificationUsers.value = true;
      const teamMembersPayload = {
        project_id: Number(projectId),
        user_ids: selectedNotificationTeamMembers.value.map((member) => Number(member.id)),
        type_id: 1,
      };
      const clientUsersPayload = {
        project_id: Number(projectId),
        user_ids: selectedNotificationClientUsers.value.map((member) => Number(member.id)),
        type_id: 2,
      };

      await Promise.all([
        API.sendQuery('/graphql/project', UPDATE_NOTIFICATION_USERS_MUTATION, teamMembersPayload),
        API.sendQuery('/graphql/project', UPDATE_NOTIFICATION_USERS_MUTATION, clientUsersPayload),
      ]);

      // await Promise.all([
      //   getTeamMembers(Number(activeAccount.value?.id), Number(activeWorkspace.value?.id)),
      //   getWorkspaceMembers(Number(activeWorkspace.value?.id), 'client'),
      // ]);
    } catch (error) {
      console.log(error);
    } finally {
      isUpdatingProjectNotificationUsers.value = false;
    }
  };
  const updateProjectGridCell = async (data: any) => {
    try {
      isGridUpdating.value = true;
      const { endpoint, query, args, key, originalKey, custom_property } = data.apiBuilder;
      let payload;

      if (custom_property) {
        // Custom properties expect value to be an array of strings
        const value = Array.isArray(data.newValue) 
          ? data.newValue 
          : [typeof data.newValue === 'boolean' ? String(data.newValue) : data.newValue];
        payload = {
          custom_properties: [{ key: originalKey, value }],
        };
      } else {
        payload = { [key]: data.newValue };
      }

      await API.sendQuery(endpoint, query, {
        ...args,
        ...payload,
      });
    } catch (error) {
      console.log(error);
    } finally {
      isGridUpdating.value = false;
    }
  };

  const updateProjectBlockedColumns = async (blockedColumns: any[]) => {
    try {
      isGridUpdating.value = true;
      const payload = {
        id: Number(activeProject.value?.id),
        custom_properties: [{ key: 'blocked_columns', value: JSON.stringify(blockedColumns) }],
      };

      await API.sendQuery('/graphql/project', UPDATE_PROJECT_MUTATION, payload);
    } catch (error) {
      console.log(error);
    } finally {
      isGridUpdating.value = false;
    }
  };

  const updateProjectView = async (payload: any) => {
    try {
      isGridUpdating.value = payload?.isGridLoading ?? true;

      const res = await API.sendQuery('/graphql/project', UPDATE_PROJECT_VIEWS_MUTATION, payload);
      if (res.data.data.updateView) {
        return formatViews([res.data.data.updateView])[0];
      }
    } catch (error) {
      console.log(error);
    } finally {
      isGridUpdating.value = false;
    }
  };

  const updateViewFilters = async (view: ProjectView, filters: any) => {
    try {
      isGridUpdating.value = true;
      const payload = {
        id: Number(view.id),
        filter: JSON.stringify(filters),
      };

      await API.sendQuery('/graphql/project', UPDATE_PROJECT_VIEWS_MUTATION, payload);
    } catch (error) {
      console.log(error);
    } finally {
      isGridUpdating.value = false;
    }
  };

  const updateViewSort = async (view: ProjectView, sort: any) => {
    try {
      isGridUpdating.value = true;
      const payload = {
        id: Number(view.id),
        sort: JSON.stringify(sort),
      };

      await API.sendQuery('/graphql/project', UPDATE_PROJECT_VIEWS_MUTATION, payload);
    } catch (error) {
      console.log(error);
    } finally {
      isGridUpdating.value = false;
    }
  };

  const updateViewColumnState = async (view: ProjectView, columnState: any) => {
    try {
      isGridUpdating.value = true;
      const payload = {
        id: Number(view.id),
        column_state: JSON.stringify(columnState),
      };

      await API.sendQuery('/graphql/project', UPDATE_PROJECT_VIEWS_MUTATION, payload);
    } catch (error) {
      console.log(error);
    } finally {
      isGridUpdating.value = false;
    }
  };

  const createProjectView = async (payload: any) => {
    try {
      const res = await API.sendQuery('/graphql/project', CREATE_PROJECT_VIEWS_MUTATION, payload);
      if (res.data.data.createView) {
        const newView = {
          ...res.data.data.createView,
          project_id: payload.project_id,
          display_mode: payload.display_mode,
        };
        return formatViews([newView])[0];
      }
    } catch (error) {
      console.log(error);
    }
  };

  const deleteProjectView = async (payload: { id: number }) => {
    try {
      const res = await API.sendQuery('/graphql/project', DELETE_PROJECT_VIEW_MUTATION, {
        id: Number(payload.id),
      });

      if (res.data.data.deleteView) {
        return true;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const removeProductFromProject = async (productId: number, product: Product) => {
    try {
      const productIndex = products.value.findIndex((p) => Number(p.id) === Number(productId));

      if (productIndex > -1) {
        products.value[productIndex].isLoading = true;
      }

      const res = await API.sendQuery('/graphql/project', REMOVE_PROJECT_PRODUCT_MUTATION, {
        project_id: Number(currentProjectID.value),
        product_id: Number(product.id),
      });
      if (res.data.data.detachProduct) {
        products.value.splice(productIndex, 1);
        console.log('removed product from project', res.data.data.detachProduct);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const deleteProject = async (project_id: number) => {
    try {
      const res = await API.sendQuery('/graphql/project', DELETE_PROJECT_MUTATION, { id: Number(project_id) });

      if (res.data.data.deleteProject) {
        return true;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const attachProjectEntityMapping = async (payload: any) => {
    try {
      const res = await API.sendQuery('/graphql/project', ATTACH_PROJECT_ENTITY_MAPPING, payload);
      if (res.data.data.attachEntityMapping) {
        return res.data.data.attachEntityMapping;
      } else {
        return null;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const detachProjectEntityMapping = async (payload: any) => {
    try {
      const res = await API.sendQuery('/graphql/project', DETACH_PROJECT_ENTITY_MAPPING, payload);
      if (res.data.data.detachEntityMapping) {
        return res.data.data.detachEntityMapping;
      } else {
        return null;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const updateProjectEntityMapping = async (payload: any) => {
    try {
      const res = await API.sendQuery('/graphql/account', UPDATE_ENTITY, payload);
      if (res.data.data.updateEntity) {
        return res.data.data.updateEntity;
      } else {
        return null;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getProjectCreatorInvites = async (project_id: number, loading: boolean = true) => {
    try {
      isLoadingCreatorInvites.value = loading;
      const res = await API.sendQuery('/graphql/project/creator', GET_PROJECT_CREATOR_INVITES_QUERY, { project_id: project_id });
      if (res.data.data.invites) {
        setProjectCreatorInvites(res.data.data.invites);
        projectCreatorInvites.value.every((element) => {
          if (element.invited !== null) {
            projectCreatorInviteSent.value = true;
          }
        });
        return res.data.data.invites;
      }
    } catch (error) {
      console.log(error);
    } finally {
      isLoadingCreatorInvites.value = false;
    }
  };

  function formatTempProducts(products: any[]): any[] {
    let data: any[] = [];
    if (products && products.length > 0) {
      data = products.map((product) => {
        return {
          ...product.product,
        };
      });
    }
    return data;
  }

  function $reset() {
    restoreGlobalAccountContext();
    currentProjectID.value = null;
    isProjectLoading.value = false;
    isFetchingProjects.value = false;
    isCreatorDetailsViewActive.value = true;
    isDocumentsViewActive.value = true;
    isDeliverablesViewActive.value = true;
    projects.value = [];
    activeProject.value = null;
    activeWorkspace.value = null;
    activeAccount.value = null;
    activeTab.value = null;
    activeTeamMembers.value = [];
    projectCreatorInvites.value = [];
    projectCreatorInviteSent.value = false;
    isLoadingCreatorInvites.value = false;
    tempEntityMappings.value = [];
    views.value = [];
    briefFiles.value = [];
    products.value = [];
    isGridUpdating.value = false;
    isCreatingProjectView.value = false;
    tempUploadedData.value = {
      creators: [],
      products: [],
      entity_mapping: [],
      lockedCustomColumns: [],
      originalCreators: [],
      originalProducts: [],
    };
    tempLockedCustomColumns.value = [];
    showViewsPanel.value = true;
    isUpdatingProjectNotificationUsers.value = false;
    selectedNotificationTeamMembers.value = [];
    selectedNotificationClientUsers.value = [];
    notificationTeamMembers.value = [];
    notificationClientUsers.value = [];
    teamMembers.value = [];
    workspaceMembers.value = [];
    showEditColumnMenu.value = false;
    columnMenuAnchor.value = null;
    columnToEdit.value = null;
  }

  return {
    currentProjectID,
    isProjectLoading,
    isFetchingProjects,
    isCreatorDetailsViewActive,
    isDocumentsViewActive,
    isDeliverablesViewActive,
    projects,
    activeProject,
    activeWorkspace,
    activeAccount,
    activeTab,
    activeTeamMembers,
    projectCreatorInvites,
    projectCreatorInviteSent,
    isLoadingCreatorInvites,
    tempEntityMappings,
    views,
    briefFiles,
    products,
    isGridUpdating,
    isCreatingProjectView,
    tempUploadedData,
    tempLockedCustomColumns,
    showViewsPanel,
    getLocalStorageViewPanel,
    toggleViewsPanel,
    setActiveProject,
    setActiveTeamMembers,
    setProjectCreatorInvites,
    setBriefFiles,
    setProducts,
    saveActiveViewInDB,
    formatViews,
    getDefaultImportSpreadsheet,
    initProject,
    syncGlobalAccountContextFromProject,
    restoreGlobalAccountContext,
    getProjectUserPermissions,
    getProject,
    getProjectProducts,
    getProjectViews,
    getProjectBriefFiles,
    downloadProjectBriefFile,
    updateProject,
    copyProject,
    updateProjectGridCell,
    updateProjectBlockedColumns,
    updateProjectView,
    updateViewFilters,
    updateViewSort,
    updateViewColumnState,
    createProjectView,
    deleteProjectView,
    removeProductFromProject,
    deleteProject,
    attachProjectEntityMapping,
    detachProjectEntityMapping,
    updateProjectEntityMapping,
    getProjectCreatorInvites,
    formatTempProducts,
    hasBrightThemeColor,
    notificationTeamMembers,
    getProjectNotificationUsers,
    getTeamMembers,
    getWorkspaceMembers,
    updateProjectNotificationUsers,
    isUpdatingProjectNotificationUsers,
    selectedNotificationTeamMembers,
    selectedNotificationClientUsers,
    teamMembers,
    workspaceMembers,
    showEditColumnMenu,
    columnMenuAnchor,
    columnToEdit,
    openMenu,
    closeMenu,
    $reset,
  };
});

export default useProjectStore;
